<template>
  <div>
    <b-modal
      id="modal-qr"
      title="Staff Registration Form QR Code"
      centered
      size="sm"
      footer-class="justify-content-center"
    >
      <qrcode-vue
        :value="qrValue"
        :size="200"
        level="H"
        class="text-center"
      ></qrcode-vue>
      <div v-if="reportDomain === 'myskooliuss'" class="mt-2">
        <p class="small text-dark mb-1 qr-dep-hint">
          Select a department before copying the link, or choose
          <strong>All Departments</strong> so applicants can pick any department
          from the open dropdown on the registration form.
        </p>
        <b-form-group
          label="Department *"
          label-class="font-weight-bold text-dark"
          invalid-feedback="Department is required to generate link."
          ref="qrDep"
        >
          <v-select
            v-model="qrDepartmentID"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="qrDepartmentOptions"
            :reduce="(val) => val.id"
            label="name"
            :clearable="false"
            :loading="depLoading"
            placeholder="Select department"
            @input="updateQrValue()"
          />
        </b-form-group>
      </div>

      <template #modal-footer>
        <b-button variant="success" class="btn-icon" @click="saveQr()">
          <feather-icon icon="DownloadIcon" />
          <span class="align-middle ml-50">Download</span>
        </b-button>
        <b-button variant="primary" class="btn-icon" @click="copyQr()">
          <feather-icon icon="CopyIcon" />
          <span class="align-middle ml-50">Copy</span>
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-filter"
      title="Filter"
      centered
      footer-class="justify-content-end"
      body-class="p-1"
    >
      <b-row>
        <b-col md="12">
          <b-form-group
            label="Applicant Status"
            label-class="font-weight-bolder text-dark mb-25 text-uppercase"
            label-style="font-size: 0.95rem;"
          >
            <v-select
              v-model="currentStatus"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="statusOptions"
              :clearable="false"
              :reduce="(val) => val.value"
              label="text"
              placeholder="Select status"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            label="Teaching Subjects"
            label-class="font-weight-bolder text-dark mb-25 text-uppercase"
            label-style="font-size: 0.95rem;"
          >
            <v-select
              v-model="subNames"
              multiple
              :closeOnSelect="false"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="subjects"
              :reduce="(opt) => opt.subject"
              label="subject"
              :clearable="false"
              placeholder="Select teaching subjects"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            label="Type (Physical/Online)"
            label-class="font-weight-bolder text-dark mb-25 text-uppercase"
            label-style="font-size: 0.95rem;"
          >
            <v-select
              v-model="currentTag"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="tagOptions"
              :clearable="false"
              :reduce="(val) => val.value"
              label="text"
              placeholder="Select type"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('Applied Date From')"
            :invalid-feedback="$t('Date is required')"
            ref="applied_from"
          >
            <flat-pickr
              :config="config"
              v-model="dtFilter.appliedFrom"
              class="form-control"
              :placeholder="$t('Select from date')"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('Applied Date Till')"
            :invalid-feedback="$t('Date is required')"
            ref="applied_to"
          >
            <flat-pickr
              :config="config"
              v-model="dtFilter.appliedTill"
              class="form-control"
              :placeholder="$t('Select to date')"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            label="Designation"
            label-class="font-weight-bolder text-dark mb-25 text-uppercase"
            label-style="font-size: 0.95rem;"
          >
            <v-select
              v-if="
                designationOptionsForFilter &&
                designationOptionsForFilter.length > 0
              "
              v-model="filterDesignation"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="designationOptionsForFilter"
              :clearable="true"
              placeholder="Select designation"
            />
            <b-form-input
              v-else
              v-model.trim="filterDesignation"
              :placeholder="$t('Enter designation')"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            label="Gender"
            label-class="font-weight-bolder text-dark mb-25 text-uppercase"
            label-style="font-size: 0.95rem;"
          >
            <v-select
              v-model="filterGender"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="genderFilterOptions"
              :clearable="false"
              :reduce="(val) => val.value"
              label="text"
              placeholder="Select gender"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <template #modal-footer>
        <b-button variant="success" @click="applyFilters()">
          <span>Show</span>
        </b-button>
      </template>
    </b-modal>

    <b-card class="all-applications-list-card">
      <div class="gradient-line-wrapper"></div>
      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 resource-title">All Applications</h5>
          <small class="text-muted"
            >Manage and review applicant information</small
          >
        </div>
      </div>

      <!-- Pagination Controls - Top (Compact Single Line) -->
      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Page Size Dropdown -->
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <!-- Count Display in Middle -->
        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="LoadData"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <!-- Search bar - Optimized grid logic -->
        <b-col
          class="mt-lg-0 mt-md-0 mt-1 d-flex align-items-center"
          :xl="
            reportDomain == 'myskoolhpgs' && $store.state.userData.cId == 1
              ? 4
              : 8
          "
          :lg="
            reportDomain == 'myskoolhpgs' && $store.state.userData.cId == 1
              ? 4
              : 8
          "
          :md="
            reportDomain == 'myskoolhpgs' && $store.state.userData.cId == 1
              ? 6
              : 8
          "
          sm="12"
          cols="12"
        >
          <b-input-group class="filter-control search-group flex-grow-1">
            <b-input-group-prepend
              is-text
              @click="loadSearch()"
              class="cursor-pointer"
            >
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              v-model.trim="searchQuery"
              placeholder="Search by name, contact..."
              @keyup.enter="loadSearch()"
            />
          </b-input-group>

          <!-- QR Button grouped with search for non-HPGS or non-admin -->
          <b-button
            v-if="
              reportDomain != 'myskoolhpgs' ||
              (reportDomain == 'myskoolhpgs' && $store.state.userData.cId != 1)
            "
            @click="openQr()"
            variant="outline-primary"
            class="btn-icon ml-1"
            v-b-tooltip.hover.top
            title="Generate QR Code"
            style="
              min-width: 46px;
              height: 46px;
              border-radius: 10px;
              border-color: #e0d8ff;
            "
          >
            <img src="@/assets/images/icons/qr-code.png" width="24" />
          </b-button>
        </b-col>

        <!-- Domain Based Campus Dropdown -->
        <b-col
          v-if="reportDomain == 'myskoolhpgs' && $store.state.userData.cId == 1"
          :xl="isFilterActive ? 3 : 4"
          :lg="isFilterActive ? 3 : 4"
          md="6"
          sm="12"
          cols="12"
          class="mb-1 mb-lg-0 mt-lg-0 mt-md-0 mt-1"
        >
          <v-select
            v-model="selectedCampus"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="campuses"
            :reduce="(val) => val.id"
            label="branch"
            :clearable="false"
            class="filter-control campus-select w-100"
            placeholder="Select campus"
            @input="LoadData('dd')"
          />
        </b-col>

        <!-- Actions: Filter and QR (HPGS Admin Context) -->
        <b-col
          v-if="reportDomain == 'myskoolhpgs' && $store.state.userData.cId == 1"
          :xl="isFilterActive ? 2 : 1"
          :lg="isFilterActive ? 2 : 1"
          md="6"
          sm="12"
          cols="10"
          class="d-flex align-items-center justify-content-start mb-1 mb-lg-0 px-1 mt-lg-0 mt-md-0 mt-1 col-auto"
        >
          <b-button
            @click="openFilter()"
            :variant="isFilterActive ? 'primary' : 'outline-primary'"
            class="action-btn-filter mr-1"
            v-b-tooltip.hover.top
            title="Filter"
            :style="isFilterActive ? '' : 'border-color: #e0d8ff;'"
          >
            <feather-icon icon="FilterIcon" />
          </b-button>
          <b-button
            v-if="isFilterActive"
            @click="clearFilters()"
            variant="outline-danger"
            class="action-btn-filter mr-1"
            v-b-tooltip.hover.top
            title="Clear Filter"
          >
            <feather-icon icon="XIcon" />
          </b-button>
          <b-button
            @click="openQr()"
            variant="outline-primary"
            class="btn-icon d-flex align-items-center justify-content-center"
            v-b-tooltip.hover.top
            title="Generate QR Code"
            style="
              min-width: 46px;
              height: 46px;
              border-radius: 10px;
              border-color: #e0d8ff;
            "
          >
            <img src="@/assets/images/icons/qr-code.png" width="28" />
          </b-button>
        </b-col>

        <!-- Export Button & Selection Info -->
        <b-col
          :xl="
            reportDomain == 'myskoolhpgs' && $store.state.userData.cId == 1
              ? 1
              : 1
          "
          lg="1"
          md="2"
          sm="2"
          cols="2"
          class="mt-lg-0 mt-md-0 mt-1 col-auto px-1 d-flex align-items-center justify-content-center"
        >
          <div class="position-relative">
            <!-- Selection Dot Badge -->
            <transition name="fade">
              <b-badge
                v-if="persistentSelectedIds.length > 0"
                variant="primary"
                class="selection-dot-badge cursor-pointer"
                @click="persistentSelectedIds = []"
                v-b-tooltip.hover.top="'Clear All Selections'"
              >
                {{ persistentSelectedIds.length }}
              </b-badge>
            </transition>

            <b-button
              @click="exportData()"
              variant="outline-primary"
              block
              class="action-btn-filter d-flex align-items-center justify-content-center"
              :disabled="exporting"
              style="min-width: 46px; border-color: #e0d8ff; background: #fff"
            >
              <b-spinner v-if="exporting" small variant="primary" />
              <feather-icon v-else icon="DownloadIcon" size="18" />
            </b-button>
          </div>
        </b-col>

        <!-- Add Button - Shifted to Right -->
        <b-col
          v-if="rights.add"
          :xl="
            reportDomain == 'myskoolhpgs' && $store.state.userData.cId == 1
              ? 2
              : 3
          "
          :lg="
            reportDomain == 'myskoolhpgs' && $store.state.userData.cId == 1
              ? 2
              : 3
          "
          md="4"
          sm="12"
          cols="12"
          class="mt-lg-0 mt-md-0 mt-1 ml-auto d-flex align-items-center"
        >
          <b-button
            @click="Edit(0)"
            variant="gradient-primary"
            block
            class="action-btn-filter text-nowrap"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          >
            <feather-icon icon="PlusIcon" class="mr-50" />
            <span>Add Applicant</span>
          </b-button>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <!-- Top Scrollbar for PC -->
        <div class="table-scrollbar-top" ref="topScrollbar">
          <div class="table-scrollbar-content"></div>
        </div>

        <!-- Table Wrapper -->
        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
          <b-table
            class="all-applications-table mt-1"
            :busy="dataLoading"
            :items="items"
            :fields="fields"
            show-empty
            :responsive="false"
            hover
            small
            :selectable="rights.edit || rights.interviewInfo || rights.approve"
            select-mode="single"
            @row-selected="Edit($event[0].id)"
          >
            <template #empty="scope">
              <h3 class="mt-1 mb-1 text-center">
                {{ $t("No records found") }}
              </h3>
            </template>
            <template #table-busy>
              <tbody>
                <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                  <td
                    v-for="(field, index) in fields"
                    :key="'skeleton-cell-' + index"
                    class="skeleton-cell"
                  >
                    <div class="skeleton-loader"></div>
                  </td>
                </tr>
              </tbody>
            </template>

            <template #head(selected)>
              <b-form-checkbox
                :checked="isAllSelected"
                :indeterminate="isIndeterminate"
                @change="selectAllRows"
              />
            </template>

            <template #cell(selected)="data">
              <b-form-checkbox
                :checked="persistentSelectedIds.includes(data.item.id)"
                @change="toggleSelection(data.item.id)"
              />
            </template>

            <template #cell(index)="data">
              {{ (currentPage - 1) * perPage + data.index + 1 }}
            </template>

            <template #cell(picture)="data">
              <b-img
                v-if="data.item.picture !== null && data.item.picture !== ''"
                rounded
                :src="getPicturePreview(data.item.picture)"
                height="80px"
                width="80px"
                alt=""
                referrerpolicy="no-referrer"
                @error="onPicturePreviewError($event, data.item.picture)"
              />
              <b-img
                v-else
                src="https://cdn.cloudious.net/file-1677912450367-333289208.png"
                height="80px"
                width="80px"
              />
            </template>

            <template #cell(name)="data">
              <b-media vertical-align="center">
                <template #aside>
                  <b-avatar
                    size="44"
                    :src="getPicturePreview(data.item.picture)"
                    variant="light-primary"
                    referrerpolicy="no-referrer"
                  />
                </template>

                <b-link class="font-weight-bold d-block text-nowrap mb-50">
                  <span class="text-primary font-weight-bolder">
                    {{ data.item.name }}
                  </span>
                </b-link>
                <b-link class="font-weight-bold d-block text-nowrap">
                  <small class="text-muted"
                    >F/N: {{ data.item.father_name }}</small
                  >
                </b-link>
              </b-media>
            </template>

            <template #cell(designation)="data">
              <b-badge variant="light-primary">
                {{ data.item.designation }}
              </b-badge>
            </template>
            <template #cell(qualification)="data">
              <b-badge variant="light-primary">
                {{ data.item.qualification }}
              </b-badge>
            </template>
            <template #cell(experience)="data">
              <b-badge variant="light-primary">
                {{ data.item.experience }}
              </b-badge>
            </template>
            <template #cell(appliedOn)="data">
              <b-badge
                variant="light-primary"
                v-if="data.item.registrationDate"
              >
                {{
                  new Date(data.item.registrationDate).toLocaleDateString(
                    "en-UK",
                    {
                      year: "numeric",
                      day: "numeric",
                      month: "short",
                    }
                  )
                }}
              </b-badge>
            </template>
            <template #cell(campus)="data">
              <b-badge variant="light-primary">
                {{ getCampus(data.item.campusID) }}
              </b-badge>
            </template>

            <template #cell(applicantStatus)="data">
              <b-badge
                :variant="
                  data.value == 'in progress'
                    ? 'light-warning'
                    : data.value == 'approved'
                    ? 'light-success'
                    : 'light-danger'
                "
                class="text-capitalize"
              >
                {{ !data.value ? "pending" : data.value }}
              </b-badge>
            </template>

            <template #head(actions)="data">
              <div class="text-center">
                <span>{{ data.label }}</span>
              </div>
            </template>

            <template #cell(actions)="data">
              <div class="d-flex justify-content-center">
                <b-button
                  v-if="
                    rights.edit &&
                    [null, '', 'pending', 'in progress'].includes(
                      data.item.applicantStatus
                    )
                  "
                  variant="outline-danger"
                  v-b-tooltip.hover.top
                  title="Reject"
                  class="action-btn-outline delete-btn mr-1"
                  @click="changeStatus(data.item)"
                >
                  <feather-icon icon="XIcon" />
                </b-button>

                <b-button
                  v-if="rights.edit || rights.interviewInfo || rights.approve"
                  variant="gradient-primary"
                  v-b-tooltip.hover.top
                  title="Edit"
                  class="action-btn-table mr-1"
                  @click="Edit(data.item.id)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>

                <b-button
                  v-if="rights.delete"
                  variant="outline-danger"
                  v-b-tooltip.hover.top
                  title="Delete"
                  class="action-btn-outline delete-btn"
                  @click="Delete(data.item.id)"
                >
                  <feather-icon icon="Trash2Icon" />
                </b-button>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BPagination,
  BImg,
  VBTooltip,
  BFormCheckbox,
  BSpinner,
  BFormGroup,
  BInputGroup,
  BInputGroupPrepend,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";

import QrcodeVue from "qrcode.vue";
import { useClipboard } from "@vueuse/core";
const { copy } = useClipboard();

import * as XLSX from "xlsx";

import { mapActions, mapGetters } from "vuex";
import {
  getMediaPreviewUrl,
  handleDrivePreviewError,
} from "@/utils/gdriveUpload";
export default {
  components: {
    BCard,
    BRow,
    BBadge,
    BTable,
    BCol,
    BFormInput,
    BButton,
    BImg,
    BMedia,
    BAvatar,
    BLink,
    BPagination,
    vSelect,
    QrcodeVue,
    BFormCheckbox,
    BSpinner,
    BFormGroup,
    BInputGroup,
    BInputGroupPrepend,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  async created() {
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
          hideFee: ch.hideFee,
          interviewInfo: ch.interviewInfo,
          approve: ch.approve,
          editCampus: ch.editCampus,
        };
      });
    });
    // console.log(right);
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({
        name: "misc-not-authorized",
      });
    } else {
      this.rights = this.$store.state.rights[this.$route.name];
      // console.log(this.rights);

      const savedFilters = localStorage.getItem("allApplicationsFilters");
      if (savedFilters) {
        try {
          const parsed = JSON.parse(savedFilters);
          this.currentStatus = parsed.currentStatus || "";
          this.subNames = parsed.subNames || [];
          this.currentTag = parsed.currentTag || "";
          this.dtFilter = parsed.dtFilter || {
            appliedFrom: null,
            appliedTill: null,
          };
          this.filterDesignation = parsed.filterDesignation || "";
          this.filterGender = parsed.filterGender || "";
        } catch (e) {
          console.error("Error parsing filters", e);
        }
      }

      this.LoadData();
      this.LoadSubjects();
      this.loadDesignationSettings();
      if (this.reportDomain === "myskooliuss") {
        this.LoadDepartments();
      }
      if (
        this.reportDomain == "myskoolhpgs" &&
        this.$store.state.userData.cId == 1
      ) {
        this.LoadCampuses();
        this.fields.splice(2, 0, {
          label: "campus",
          key: "campus",
          thClass: "sticky-campus",
          tdClass: "sticky-campus",
        });

        let nameField = this.fields.find((f) => f.key === "name");
        if (nameField) {
          nameField.thClass = "sticky-name-campus";
          nameField.tdClass = "sticky-name-campus";
        }

        // Adjust campus sticky offset
        let campusField = this.fields.find((f) => f.key === "campus");
        if (campusField) {
          campusField.thClass = "sticky-campus";
          campusField.tdClass = "sticky-campus";
        }
      }
    }
  },
  mounted() {
    this.updateScrollbarWidth();
    window.addEventListener("resize", this.updateScrollbarWidth);
  },
  updated() {
    this.updateScrollbarWidth();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScrollbarWidth);
  },
  computed: {
    isFilterActive() {
      return (
        (this.currentStatus && this.currentStatus !== "") ||
        (this.subNames && this.subNames.length > 0) ||
        (this.currentTag && this.currentTag !== "") ||
        this.dtFilter.appliedFrom ||
        this.dtFilter.appliedTill ||
        (this.filterDesignation && this.filterDesignation.trim() !== "") ||
        (this.filterGender && this.filterGender !== "")
      );
    },
    designationOptionsForFilter() {
      return this.getDesignationNamesForDepartment(0);
    },
    displayedRange() {
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start: Math.max(0, start), end: Math.max(0, end) };
    },
    filters: function () {
      return this.items.filter((pro) => {
        return pro.name.toLowerCase().match(this.searchQuery.toLowerCase());
      });
    },
    isAllSelected() {
      if (this.items.length === 0) return false;
      return this.items.every((item) =>
        this.persistentSelectedIds.includes(item.id)
      );
    },
    isIndeterminate() {
      const selectedCount = this.items.filter((item) =>
        this.persistentSelectedIds.includes(item.id)
      ).length;
      return selectedCount > 0 && selectedCount < this.items.length;
    },
    qrDepartmentOptions() {
      if (this.reportDomain !== "myskooliuss") return this.departments;
      return [{ id: 0, name: "All Departments" }, ...(this.departments || [])];
    },
  },
  data() {
    return {
      rights: {},
      config: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      filterloading: false,
      filter: {
        class: 0,
        section: "",
      },
      exporting: false,
      idList: [],
      persistentSelectedIds: [],
      dataLoading: false,
      searchQuery: "",
      currentPage: 1,
      perPage: 50,
      totalCount: 0,
      currentStatus: "",
      currentTag: "",
      tagOptions: [
        { text: "All", value: "" },
        { text: "Physical", value: "Physical" },
        { text: "Online", value: "Online" },
      ],
      statusOptions: [
        { text: "All", value: "" },
        { text: "Pending", value: "pending" },
        { text: "In Progress", value: "in progress" },
        { text: "Rejected", value: "rejected" },
      ],
      fields: [
        {
          label: "",
          key: "selected",
          thClass: "sticky-sno",
          tdClass: "sticky-sno",
        },
        {
          label: "S.No",
          key: "index",
          thClass: "sticky-sno-2",
          tdClass: "sticky-sno-2",
        },
        {
          label: "name",
          key: "name",
          thClass: "sticky-name",
          tdClass: "sticky-name",
        },
        // { label: "father name", key: "father_name" },
        { label: "contact", key: "contact_number" },
        { label: "qualification", key: "qualification" },
        { label: "designation", key: "designation" },
        { label: "experience", key: "experience" },
        { label: "applied on", key: "appliedOn" },
        { label: "status", key: "applicantStatus" },
        "actions",
      ],
      items: [],
      allItems: [],
      selectedCampus: this.$store.state.userData.cId,
      campuses: [],
      reportDomain: this.$store.state.report.replace(/_/g, ""),
      qrValue: "",
      qrDepartmentID: null,
      departments: [],
      depLoading: false,
      subjects: [],
      subNames: [],
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      dtFilter: {
        appliedFrom: null,
        appliedTill: null,
      },
      filterDesignation: "",
      filterGender: "",
      genderFilterOptions: [
        { text: "All", value: "" },
        { text: "Male", value: "Male" },
        { text: "Female", value: "Female" },
      ],
      designations: [],
      designationsLoaded: false,
      designationSetting: null,
      loadingDesignationSettings: false,
    };
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    getPicturePreview(url) {
      if (!url) return "";
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },

    onPicturePreviewError(event, url) {
      if (!url || String(url).startsWith("blob:")) return;
      handleDrivePreviewError(event, url, "image");
    },

    applyFilters() {
      const filters = {
        currentStatus: this.currentStatus,
        subNames: this.subNames,
        currentTag: this.currentTag,
        dtFilter: this.dtFilter,
        filterDesignation: this.filterDesignation || "",
        filterGender: this.filterGender || "",
      };
      localStorage.setItem("allApplicationsFilters", JSON.stringify(filters));
      this.LoadData("dd");
    },
    clearFilters() {
      this.currentStatus = "";
      this.subNames = [];
      this.currentTag = "";
      this.dtFilter = {
        appliedFrom: null,
        appliedTill: null,
      };
      this.filterDesignation = "";
      this.filterGender = "";
      localStorage.removeItem("allApplicationsFilters");
      this.LoadData("dd");
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.LoadData();
      this.$nextTick(() => {
        this.updateScrollbarWidth();
      });
    },

    syncScroll() {
      if (this.$refs.tableWrapper && this.$refs.topScrollbar) {
        const tableWrapper = this.$refs.tableWrapper;
        const topScrollbar = this.$refs.topScrollbar;
        const scrollContent = topScrollbar.querySelector(
          ".table-scrollbar-content"
        );

        if (scrollContent) {
          topScrollbar.scrollLeft = tableWrapper.scrollLeft;
        }
      }
    },

    updateScrollbarWidth() {
      this.$nextTick(() => {
        if (this.$refs.tableWrapper && this.$refs.topScrollbar) {
          const tableWrapper = this.$refs.tableWrapper;
          const topScrollbar = this.$refs.topScrollbar;
          const scrollContent = topScrollbar.querySelector(
            ".table-scrollbar-content"
          );

          if (scrollContent) {
            if (tableWrapper.scrollWidth > tableWrapper.clientWidth) {
              scrollContent.style.width = `${tableWrapper.scrollWidth}px`;
              topScrollbar.style.display = "block";

              topScrollbar.onscroll = () => {
                tableWrapper.scrollLeft = topScrollbar.scrollLeft;
              };
            } else {
              topScrollbar.style.display = "none";
            }
          }
        }
      });
    },

    isQrDepartmentSelected() {
      return this.qrDepartmentID !== null && this.qrDepartmentID !== undefined;
    },
    encodeQrDepartmentId(id) {
      return encodeURIComponent(`??${id}%24`);
    },
    buildQrUrl() {
      const db = encodeURIComponent(this.$store.state.userData.db);
      let url = `${window.location.origin}/staff-registration?db=${db}&cId=${this.$store.state.userData.cId}`;
      if (this.reportDomain === "myskooliuss") {
        url += "&domain=myskooliuss";
        if (this.qrDepartmentID > 0) {
          url += `&dpt=${this.encodeQrDepartmentId(this.qrDepartmentID)}`;
        }
      }
      return url;
    },
    openQr() {
      if (this.reportDomain === "myskooliuss" && !this.departments.length) {
        this.LoadDepartments();
      }
      this.qrValue = this.buildQrUrl();
      this.$bvModal.show("modal-qr");
    },
    copyQr() {
      if (
        this.reportDomain === "myskooliuss" &&
        !this.isQrDepartmentSelected()
      ) {
        if (this.$refs.qrDep) this.$refs.qrDep.state = false;
        return this.$bvToast.toast(
          "Please select a department before copying the link.",
          {
            title: "Department required",
            variant: "danger",
            toaster: "b-toaster-top-center",
            solid: true,
          }
        );
      }
      this.qrValue = this.buildQrUrl();
      copy(this.qrValue);
      this.$bvToast.toast("Link Copied.", {
        title: "Copied!",
        variant: "success",
        toaster: "b-toaster-top-center",
        solid: true,
      });
    },
    updateQrValue() {
      this.qrValue = this.buildQrUrl();
      if (this.$refs.qrDep) this.$refs.qrDep.state = undefined;
    },
    async LoadDepartments() {
      this.depLoading = true;
      try {
        this.departments = await this.get({
          url:
            this.$store.state.domain +
            "StaffDepartments?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        });
      } finally {
        this.depLoading = false;
      }
    },
    getDesignationNamesForDepartment(departmentID) {
      const depId = Number(departmentID) || 0;
      return (this.designations || [])
        .filter((item) => {
          const itemDepId = Number(item.departmentID) || 0;
          if (!depId) return true;
          return itemDepId === depId || itemDepId === 0;
        })
        .map((item) => item.name)
        .filter((name) => !!name);
    },
    normalizeDesignations(rawValue) {
      if (!rawValue) return [];

      if (typeof rawValue === "string" && rawValue.trim().startsWith("[")) {
        try {
          const parsed = JSON.parse(rawValue);
          if (Array.isArray(parsed)) {
            return parsed
              .map((item) => {
                if (typeof item === "string") {
                  return { name: item.trim(), departmentID: 0 };
                }
                return {
                  name: String(item.name || "").trim(),
                  departmentID: Number(item.departmentID) || 0,
                };
              })
              .filter((item) => item.name);
          }
        } catch (e) {
          // fall through
        }
      }

      if (typeof rawValue === "string") {
        return rawValue
          .split(",")
          .map((name) => name.trim())
          .filter((name) => name)
          .map((name) => ({ name, departmentID: 0 }));
      }

      if (Array.isArray(rawValue)) {
        return rawValue
          .map((item) => {
            if (typeof item === "string") {
              return { name: item.trim(), departmentID: 0 };
            }
            return {
              name: String(item.name || "").trim(),
              departmentID: Number(item.departmentID) || 0,
            };
          })
          .filter((item) => item.name);
      }

      return [];
    },
    async loadDesignationSettings(force = false) {
      if (this.loadingDesignationSettings) return;
      if (this.designationsLoaded && !force) return;

      this.loadingDesignationSettings = true;
      try {
        const response = await this.post({
          url: `${this.$store.state.domain}Settings/getSettingsAll?db=${this.$store.state.userData.db}`,
          body: ["staff-designations"],
          context: this,
          token: this.$store.state.userData.token,
          subjects: true,
        });

        let settingsArray = [];
        if (
          response &&
          response.status === "success" &&
          response.data &&
          Array.isArray(response.data)
        ) {
          settingsArray = response.data;
        } else if (Array.isArray(response)) {
          settingsArray = response;
        }

        this.designationSetting =
          settingsArray.find(
            (setting) =>
              setting.key === "staff-designations" &&
              Number(setting.campusID) === Number(this.$store.state.userData.cId)
          ) ||
          settingsArray.find((setting) => setting.key === "staff-designations");

        if (this.designationSetting && this.designationSetting.value) {
          this.designations = this.normalizeDesignations(
            this.designationSetting.value
          );
        } else {
          this.designations = [];
        }
        this.designationsLoaded = true;
      } catch (error) {
        console.error("Error loading designation settings:", error);
        this.designations = [];
      } finally {
        this.loadingDesignationSettings = false;
      }
    },
    saveQr() {
      if (
        this.reportDomain === "myskooliuss" &&
        !this.isQrDepartmentSelected()
      ) {
        if (this.$refs.qrDep) this.$refs.qrDep.state = false;
        return this.$bvToast.toast(
          "Please select a department before downloading the QR code.",
          {
            title: "Department required",
            variant: "danger",
            toaster: "b-toaster-top-center",
            solid: true,
          }
        );
      }
      this.qrValue = this.buildQrUrl();
      const canvas = document.querySelector("#modal-qr canvas");
      let image = canvas.toDataURL();
      let dLink = document.createElement("a");
      dLink.download = "staff-registration.png";
      dLink.href = image;
      dLink.click();
    },
    openFilter() {
      this.$bvModal.show("modal-filter");
    },

    filterData() {
      if (this.selectedCampus == 0) {
        this.items = this.allItems;
      } else {
        this.items = this.allItems.filter(
          (el) => el.campusID == this.selectedCampus
        );
      }
      // console.log(this.items);
      if (this.currentStatus != "") this.filterByStatus();
    },
    filterByStatus() {
      if ([null, "", "pending"].includes(this.currentStatus)) {
        this.items = this.items.filter((el) =>
          [null, "", "pending"].includes(el.applicantStatus)
        );
      } else {
        this.items = this.items.filter(
          (el) => el.applicantStatus == this.currentStatus
        );
      }
    },
    getCampus(id) {
      let obj = this.campuses.find((el) => el.id == id);
      return obj ? obj.branch : "";
    },
    async LoadCampuses() {
      var obj = {
        url:
          this.$store.state.domain +
          "campuses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.campuses = await this.get(obj);
      // console.log(this.campuses);
      // this.campuses.unshift({ id: 0, branch: "All Campuses" });
      // this.selectedCampus = 0;
    },
    Edit(id) {
      // console.log("id", id);
      this.$router.push({
        name: "staff-application",
        params: {
          id: id,
        },
      });
    },
    async changeStatus(item) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: "Do you want to reject this application?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, Reject it!",
      });

      if (result.isConfirmed) {
        // console.log(item);
        item.applicantStatus = "rejected";
        var res = await this.put({
          url:
            this.$store.state.domain +
            "staff/Update?db=" +
            this.$store.state.userData.db +
            "&userID=" +
            this.$store.state.userData.userID,
          body: item,
          message: "Applicant status updated successfully!",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (res) {
          this.LoadData();
        }
      }
    },
    async Delete(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "staff/Delete?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&id=" +
            id +
            "&userID=" +
            this.$store.state.userData.userID,
          message: "Applicant removed successfully.",
          context: this,
          body: null,
          token: this.$store.state.userData.token,
        });
        if (status) this.LoadData();
      }
    },

    async LoadSubjects() {
      var obj = {
        url:
          this.$store.state.domain +
          "Subjects?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.subjects = await this.get(obj);
    },
    async LoadData(text) {
      this.dataLoading = true;
      // let myitem = await this.get({
      //   url:
      //     this.$store.state.domain +
      //     "staff/LoadApplicants?db=" +
      //     this.$store.state.userData.db +
      //     "&cId=" +
      //     this.$store.state.userData.cId,
      //   token: this.$store.state.userData.token,
      // });
      this.$bvModal.hide("modal-filter");
      if (text == "dd") {
        this.currentPage = 1;
      }
      let myitem = await this.get({
        url:
          this.$store.state.domain +
          "staff/LoadDataPagination?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.selectedCampus +
          "&dptID=0&status=applicant&page=" +
          this.currentPage +
          "&applicantStatus=" +
          this.currentStatus +
          "&subs=" +
          this.subNames +
          "&tag=" +
          this.currentTag +
          "&appliedFrom=" +
          (this.dtFilter.appliedFrom || "") +
          "&appliedTill=" +
          (this.dtFilter.appliedTill || "") +
          "&designation=" +
          encodeURIComponent(this.filterDesignation || "") +
          "&gender=" +
          encodeURIComponent(this.filterGender || "") +
          "&sortBy=appliedon&sortOrder=desc",
        token: this.$store.state.userData.token,
        dashboard: true,
      });
      // console.log(myitem);
      this.items = myitem.data;
      this.totalCount = myitem.count;
      this.allItems = myitem.data;

      this.dataLoading = false;
      // console.log("items", this.items);
    },
    toggleSelection(id) {
      const index = this.persistentSelectedIds.indexOf(id);
      if (index === -1) {
        this.persistentSelectedIds.push(id);
      } else {
        this.persistentSelectedIds.splice(index, 1);
      }
      // Sync with idList if needed (like staffGrid.vue)
      this.idList = this.items
        .filter((item) => this.persistentSelectedIds.includes(item.id))
        .map((item) => item.id);
    },
    updatePersistentSelections() {
      // Get IDs of items on current page that are currently selected
      const currentPageSelectedIds = this.idList;

      // Get IDs of items on current page that are not currently selected (deselected)
      const currentPageDeselectedIds = this.items
        .filter((item) => this.persistentSelectedIds.includes(item.id))
        .map((item) => item.id)
        .filter((id) => !currentPageSelectedIds.includes(id));

      // Remove deselected items from persistent storage
      this.persistentSelectedIds = this.persistentSelectedIds.filter(
        (id) => !currentPageDeselectedIds.includes(id)
      );

      // Add newly selected items to persistent storage
      const newSelections = currentPageSelectedIds.filter(
        (id) => !this.persistentSelectedIds.includes(id)
      );
      this.persistentSelectedIds = [
        ...this.persistentSelectedIds,
        ...newSelections,
      ];
    },
    selectAllRows(checked) {
      if (checked) {
        this.items.forEach((item) => {
          if (!this.persistentSelectedIds.includes(item.id)) {
            this.persistentSelectedIds.push(item.id);
          }
        });
      } else {
        const currentIds = this.items.map((item) => item.id);
        this.persistentSelectedIds = this.persistentSelectedIds.filter(
          (id) => !currentIds.includes(id)
        );
      }
    },
    async exportData() {
      this.exporting = true;
      try {
        let exportItems = [];
        if (this.persistentSelectedIds.length > 0) {
          // Fetch selected records using LoadDataSelected API
          const response = await this.get({
            url:
              this.$store.state.domain +
              "staff/LoadDataSelected?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.$store.state.userData.cId +
              "&tIDs=" +
              this.persistentSelectedIds.join(","),
            token: this.$store.state.userData.token,
          });

          if (response && (response.data || Array.isArray(response))) {
            exportItems = response.data || response;
          } else {
            // Fallback to current view if API fails
            exportItems = this.items.filter((item) =>
              this.persistentSelectedIds.includes(item.id)
            );
          }
        } else {
          // Export everything filtered (fetch a large page)
          const response = await this.get({
            url:
              this.$store.state.domain +
              "staff/LoadDataPagination?db=" +
              this.$store.state.userData.db +
              "&cId=" +
              this.selectedCampus +
              "&dptID=0&status=applicant&page=1&pageSize=10000" +
              "&applicantStatus=" +
              this.currentStatus +
              "&subs=" +
              this.subNames +
              "&tag=" +
              this.currentTag +
              "&appliedFrom=" +
              (this.dtFilter.appliedFrom || "") +
              "&appliedTill=" +
              (this.dtFilter.appliedTill || "") +
              "&designation=" +
              encodeURIComponent(this.filterDesignation || "") +
              "&gender=" +
              encodeURIComponent(this.filterGender || "") +
              "&sortBy=appliedon&sortOrder=desc",
            token: this.$store.state.userData.token,
            dashboard: true,
          });

          if (response && response.data && Array.isArray(response.data)) {
            exportItems = response.data;
          } else if (Array.isArray(response)) {
            exportItems = response;
          } else if (response && response.items) {
            exportItems = response.items;
          }
        }

        if (exportItems.length === 0) {
          this.$bvToast.toast("No records found to export", {
            variant: "warning",
            solid: true,
          });
          return;
        }

        const dataToExport = exportItems.map((item) => ({
          ID: item.id,
          Name: item.name,
          "Father Name": item.father_name,
          Contact: item.contact_number,
          Qualification: item.qualification,
          Experience: item.experience,
          Designation: item.designation,
          "Applied on": item.registrationDate,
          Status: item.applicantStatus || "Pending",
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Applicants");
        XLSX.writeFile(wb, "Applicants_Export.xlsx");

        this.$bvToast.toast("Export successful", {
          variant: "success",
          solid: true,
        });
      } catch (err) {
        console.error(err);
        this.$bvToast.toast("Export failed", {
          variant: "danger",
          solid: true,
        });
      } finally {
        this.exporting = false;
      }
    },
    async loadSearch() {
      if (this.searchQuery.trim() == "") {
        this.currentPage = 1;
        this.LoadData();
      } else {
        this.dataLoading = true;
        let myitem = await this.get({
          url:
            this.$store.state.domain +
            "staff/Search?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&keyword=" +
            this.searchQuery +
            "&status=applicant",
          token: this.$store.state.userData.token,
        });
        this.items = myitem;
        this.totalCount = 0;
        this.currentPage = 1;
        this.dataLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.qr-dep-hint {
  color: #000 !important;
}

.all-applications-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;

  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #7367f0 0%, #9e95f5 100%);
  }
}

.resource-title {
  color: #2d1b69;
  font-weight: 800;
  letter-spacing: -0.5px;
  font-size: 1.4rem;
}

.filter-row {
  padding: 0.5rem 0;
  border-radius: 15px;
}

.filter-control {
  &.search-group {
    ::v-deep .input-group-prepend {
      margin-right: -1px; // Ensure overlap to remove white shadow gap
      .input-group-text {
        background: #ffffff;
        border: 1.5px solid #e0d8ff;
        border-right: none;
        border-radius: 12px 0 0 12px;
        color: #7367f0;
        padding-left: 1.25rem;
        transition: all 0.3s ease;
        box-shadow: none; // Remove potential shadows
      }
    }
    ::v-deep .form-control {
      border-left: none;
      padding-left: 0.5rem;
      border-top-left-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
    }
  }

  ::v-deep .form-control {
    height: 46px;
    border-radius: 12px;
    border: 1.5px solid #e0d8ff;
    padding-left: 1.25rem;
    font-weight: 500;
    color: #2d1b69;
    transition: all 0.3s ease;

    &::placeholder {
      color: #5e5873; // Blackish/Dark neutral color
      font-weight: 500;
      opacity: 0.8;
    }

    &:focus {
      border-color: #7367f0;
      box-shadow: 0 4px 12px rgba(115, 103, 240, 0.12);
      background: #fff;
    }
  }

  &.campus-select {
    ::v-deep .vs__dropdown-toggle {
      height: 46px;
      border-radius: 12px;
      border: 1.5px solid #e0d8ff;
      background: #ffffff;
      padding-top: 2px;
      transition: all 0.3s ease;

      &:hover {
        border-color: #7367f0;
      }
    }
    ::v-deep .vs__selected {
      font-weight: 500;
      color: #2d1b69;
    }
    ::v-deep .vs__search::placeholder {
      color: #b4a9e0;
    }
  }

  ::v-deep .form-control {
    height: 46px;
    border-radius: 10px;
    border: 1.5px solid #e0d8ff;
    padding-left: 1rem;
    font-weight: 500;
    transition: all 0.3s ease;

    &:focus {
      border-color: #7367f0;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1);
    }
  }
}

.action-btn-filter {
  height: 46px;
  font-weight: 600;
  border-radius: 10px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.2);
  }
}

.selection-dot-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  min-width: 22px;
  height: 22px;
  border-radius: 10px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  padding: 0 6px !important;
  font-size: 0.7rem !important;
  z-index: 10;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background: #7367f0 !important;
  color: #fff !important;
}

.all-applications-table {
  margin-top: 1rem;
  border-radius: 12px;
  min-width: 100%;
  width: max-content;
  border: 1px solid #e5e7eb;
  border-collapse: separate;
  border-spacing: 0;

  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 5;

    &::after {
      display: none;
    }
  }

  ::v-deep td {
    vertical-align: middle;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f8fafc;
    padding: 0.75rem 0.875rem;
    background: #ffffff;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    font-size: 0.875rem;
    color: #475569;
    white-space: nowrap;
  }

  ::v-deep tbody tr {
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    border-left: 2px solid transparent;

    &:nth-child(even) {
      background: #f8fafc;
      td {
        background: #f8fafc;
      }
    }

    &:hover {
      background: #f3f0ff !important;
      border-left-color: #7c5dfa;
      td {
        background: #f3f0ff !important;
        color: #2d1b69;
      }
    }
  }

  @media (min-width: 992px) {
    ::v-deep .sticky-sno,
    ::v-deep .sticky-sno-2,
    ::v-deep .sticky-name,
    ::v-deep .sticky-campus,
    ::v-deep .sticky-name-campus {
      position: -webkit-sticky !important;
      position: sticky !important;
      z-index: 2 !important;
      background: #fff;
    }

    ::v-deep th.sticky-sno,
    ::v-deep th.sticky-sno-2,
    ::v-deep th.sticky-name,
    ::v-deep th.sticky-campus,
    ::v-deep th.sticky-name-campus {
      z-index: 15 !important;
      background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%) !important;
    }

    ::v-deep .sticky-sno {
      left: 0 !important;
      min-width: 40px;
      max-width: 40px;
      text-align: center;
    }

    ::v-deep .sticky-sno-2 {
      left: 40px !important;
      min-width: 60px;
      max-width: 60px;
    }

    ::v-deep .sticky-campus {
      left: 100px !important;
      min-width: 250px;
      max-width: 250px;
    }

    ::v-deep .sticky-name {
      left: 100px !important;
      min-width: 250px;
      max-width: 250px;
    }

    ::v-deep .sticky-name-campus {
      left: 350px !important;
      min-width: 250px;
      max-width: 250px;
    }

    // Match table striping for sticky cells
    ::v-deep tbody tr:nth-child(even) {
      .sticky-sno,
      .sticky-sno-2,
      .sticky-name,
      .sticky-campus,
      .sticky-name-campus {
        background: #f8fafc !important;
      }
    }

    // Match table hover for sticky cells
    ::v-deep tbody tr:hover {
      .sticky-sno,
      .sticky-sno-2,
      .sticky-name,
      .sticky-campus,
      .sticky-name-campus {
        background: #f3f0ff !important;
      }
    }
  }
}

.action-btn-outline {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: #fff !important;

  &.delete-btn {
    border: 2px solid #ef4444 !important;
    color: #ef4444 !important;
    &:hover {
      background: #fef2f2 !important;
      transform: translateY(-2px);
    }
  }
}

.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
  }
}

// Pagination Controls Styles
.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
  }
}

// Compact Page Size Select
.page-size-select-compact {
  min-width: 75px;
  width: 75px;

  ::v-deep .vs__dropdown-toggle {
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 6px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0 0.5rem !important;
    background: #fff !important;
    font-size: 0.8125rem !important;
    display: flex !important;
    align-items: center !important;

    &:hover {
      border-color: #b8a9ff !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.1) !important;
    }
  }

  ::v-deep .vs__selected-options {
    display: flex !important;
    align-items: center !important;
    padding: 0 !important;
  }

  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 600 !important;
    font-size: 0.8125rem !important;
    line-height: 1 !important;
    display: flex !important;
    align-items: center !important;
    height: 100%;
    visibility: visible !important;
    opacity: 1 !important;
  }

  ::v-deep .vs__search {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  ::v-deep .vs__actions {
    padding: 0 0.25rem 0 0.5rem !important;
  }

  ::v-deep .vs__dropdown-menu {
    z-index: 99999 !important;
    position: fixed !important;
    min-width: 75px !important;
    width: 75px !important;
    border-radius: 8px !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    margin-top: 4px !important;
    background: #ffffff !important;
  }
}

.pagination-count-display {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  margin: 0 1rem;
  min-width: 200px;

  .count-text {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
    white-space: nowrap;

    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }
}

.pagination-wrapper-compact {
  display: flex;
  align-items: center;

  ::v-deep .pagination {
    margin-bottom: 0;

    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      line-height: 1.5;
      border-radius: 6px;
      margin: 0 0.125rem;
      border: 1px solid #e0d8ff;
      color: #64748b;
      font-weight: 500;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;

      &:hover {
        background: #f3f0ff;
        border-color: #b8a9ff;
        color: #7c5dfa;
      }
    }

    .page-item.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
    }

    .page-item.disabled .page-link {
      background: #f8fafc;
      border-color: #e0d8ff;
      color: #cbd5e1;
      cursor: not-allowed;
    }
  }
}

// Table Wrapper for Horizontal Scroll
.table-scroll-container {
  position: relative;
  width: 100%;
}

.table-wrapper {
  overflow-x: auto;
  width: 100%;
  scrollbar-width: none; // Safari/Chrome handle below

  &::-webkit-scrollbar {
    display: none;
  }
}

// Top Scrollbar for PC Users
.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;

  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }

  .table-scrollbar-content {
    height: 1px;
    width: 100%;
  }

  @media (max-width: 768px) {
    display: none;
  }
}

// Skeleton Loader Styles
.skeleton-row {
  animation: skeletonPulse 2s ease-in-out infinite;
  border-bottom: 1px solid #f1f5f9;
}

.skeleton-cell {
  padding: 1.25rem 1rem !important;
  background: #fff;
  vertical-align: middle;

  &:nth-child(1) .skeleton-loader {
    width: 40px;
    margin: 0 auto;
  }
  &:nth-child(2) .skeleton-loader {
    width: 220px;
  }
  &:nth-child(3) .skeleton-loader {
    width: 120px;
  }
  &:nth-child(4) .skeleton-loader {
    width: 140px;
  }
  &:nth-child(5) .skeleton-loader {
    width: 100px;
  }
  &:nth-child(6) .skeleton-loader {
    width: 100px;
  }
  &:nth-child(7) .skeleton-loader {
    width: 80px;
  }
  &:nth-child(8) .skeleton-loader {
    width: 80px;
    margin: 0 auto;
  }
}

.skeleton-loader {
  height: 18px;
  border-radius: 10px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #f8f8f8 25%,
    #f0f0f0 50%,
    #f8f8f8 75%,
    #f0f0f0 100%
  );
  background-size: 300% 100%;
  animation: skeletonShimmer 2s ease-in-out infinite;
  width: 100%;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.4) 50%,
      transparent 100%
    );
    animation: skeletonShine 2s ease-in-out infinite;
  }
}

@keyframes skeletonPulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(0.998);
  }
}

@keyframes skeletonShimmer {
  0% {
    background-position: -300% 0;
  }
  100% {
    background-position: 300% 0;
  }
}

@keyframes skeletonShine {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

@media (max-width: 992px) {
  .pagination-controls-top {
    flex-wrap: wrap;
    gap: 0.75rem;
    justify-content: center;

    .pagination-count-display {
      order: 3;
      width: 100%;
      margin: 0.5rem 0 0 0;
      text-align: center;
    }
  }
}

@media (max-width: 768px) {
  .filter-row {
    > div {
      margin-bottom: 0.75rem;
    }
  }

  .all-applications-list-card {
    padding: 1rem;
    border-radius: 15px;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
